// Flow (zoom level 2): swimlanes by stage → step cards (engine state, reason, owner) → the queue's tasks under each step
// (owner person / agent / client), open items and stale flags; a kanban view by step state; and the dependency view with the
// engine's stale causes. Cards open a drawer with the step's detail and its engine actions. Nothing is dragged: status changes
// only through the engine's gates.
//
// Tasks are placed under the step whose published `commands` include the task's command (the engine publishes no stage on an
// action yet — a join for display, never a status). Tasks that match no step are listed in their own lane.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../app/context";
import { AgentChip, Card, DataTable, Drawer, SignoffCircles, StatusPill, Swimlane } from "../../components";
import type { Action, OpenItem, Stage, StageStep, Workpaper } from "../../data/types";
import { actionPayload } from "../../lib/commands";
import { titleCase } from "../../lib/format";
import { usePref } from "../../lib/hooks";
import { useEngagement } from "./Layout";
import { Act, QueueList, useAct, type Open } from "./shared";

type View = "lanes" | "kanban" | "deps";
const KANBAN_ORDER = ["in_progress", "waiting_on_client", "waiting_on_review", "blocked", "cannot_run", "not_started", "done", "not_applicable", "not_tracked"];

/** Place each action under the first step (published order) whose commands name it; open steps are preferred. */
export function placeTasks(stages: Stage[], actions: Action[]): { byStep: Map<string, Action[]>; unplaced: Action[] } {
  const steps = stages.flatMap((s) => s.steps);
  const open = steps.filter((s) => s.state !== "done" && s.state !== "not_applicable");
  const byStep = new Map<string, Action[]>();
  const unplaced: Action[] = [];
  for (const a of actions) {
    const s = open.find((x) => x.commands.includes(a.command)) ?? steps.find((x) => x.commands.includes(a.command));
    if (!s) { unplaced.push(a); continue; }
    byStep.set(s.stepId, [...(byStep.get(s.stepId) ?? []), a]);
  }
  return { byStep, unplaced };
}

const ownerKind = (a: Action) => (a.kind === "client" ? "client" : a.human ? "person" : "agent");

export function FlowPage() {
  const { model, id } = useEngagement();
  const { open, drawer } = useAct();
  const [view, setView] = usePref<View>("flow:view", "lanes");
  const [showDone, setShowDone] = useState(false);
  const [step, setStep] = useState<StageStep | null>(null);
  const { byStep, unplaced } = useMemo(() => placeTasks(model.stages, model.actions), [model.stages, model.actions]);
  const items = model.openItems.filter((i) => i.status === "open");
  const itemsFor = (s: StageStep) => items.filter((i) => i.source && s.commands.includes(i.source));
  const staleFor = (s: StageStep) => model.workpapers.filter((w) => w.stale && s.templates.includes(w.template));
  const stages = model.stages.filter((s) => s.stageId.startsWith("S"));

  const card = (s: StageStep) => <StepCard s={s} tasks={byStep.get(s.stepId) ?? []} items={itemsFor(s)} stale={staleFor(s)} onOpen={() => setStep(s)} />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Flow view" className="inline-flex rounded-md border border-line p-0.5">
          {([["lanes", "Swimlanes"], ["kanban", "Kanban by status"], ["deps", "Dependencies"]] as const).map(([v, l]) => (
            <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}
              className={`rounded px-2.5 py-1 text-sm ${view === v ? "bg-brand/10 font-semibold text-brand" : "text-muted hover:text-ink"}`}>{l}</button>
          ))}
        </div>
        {view !== "deps" && (
          <label className="inline-flex items-center gap-1.5 text-sm text-muted"><input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> Show done steps</label>
        )}
        <span className="text-xs text-muted">Status changes only through the engine's gates — cards open the step's actions.</span>
      </div>

      {view === "lanes" && (
        <Swimlane<StageStep | Action>
          lanes={[
            ...stages.filter((s) => showDone || s.steps.some((x) => x.state !== "done" && x.state !== "not_applicable")).map((s) => ({
              id: s.stageId, title: `${s.stageId} ${s.name.split(":")[0]}`, subtitle: <span className="inline-flex flex-wrap items-center gap-1"><StatusPill state={s.state} />{s.reason && s.state !== "done" ? <span title={s.reason} className="line-clamp-2">{s.reason}</span> : null}</span>,
              items: s.steps.filter((x) => showDone || (x.state !== "done" && x.state !== "not_applicable")),
            })),
            ...(unplaced.length ? [{ id: "unplaced", title: "Tasks not tied to a step", items: unplaced as Array<StageStep | Action> }] : []),
          ]}
          cardKey={(c) => ("stepId" in c ? c.stepId : `a:${c.key}`)} emptyText="All steps done"
          renderCard={(c) => ("stepId" in c ? card(c) : <TaskRow a={c} open={open} />)} />
      )}

      {view === "kanban" && (
        <Swimlane<StageStep>
          lanes={KANBAN_ORDER.filter((st) => showDone || !["done", "not_applicable", "not_tracked"].includes(st))
            .concat([...new Set(stages.flatMap((s) => s.steps.map((x) => x.state)))].filter((st) => !KANBAN_ORDER.includes(st)))
            .map((st) => ({ id: st, title: titleCase(st), subtitle: <StatusPill state={st} />, items: stages.flatMap((s) => s.steps).filter((x) => x.state === st) }))}
          cardKey={(s) => s.stepId} emptyText="None" renderCard={card} />
      )}

      {view === "deps" && <Dependencies wps={model.workpapers} id={id} />}

      <Drawer open={!!step} onClose={() => setStep(null)} title={step ? `${step.stepId} ${step.name}` : ""}>
        {step && <StepDetail s={step} tasks={byStep.get(step.stepId) ?? []} items={itemsFor(step)} wps={model.workpapers} id={id} open={(...a) => { setStep(null); open(...a); }} />}
      </Drawer>
      {drawer}
    </div>
  );
}

function StepCard({ s, tasks, items, stale, onOpen }: { s: StageStep; tasks: Action[]; items: OpenItem[]; stale: Workpaper[]; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen} className="block w-full space-y-1 text-left" aria-label={`${s.stepId} ${s.name}: ${titleCase(s.state)}${s.reason ? ` — ${s.reason}` : ""}. Open the step`}>
      <span className="flex items-center justify-between gap-2"><span className="font-mono text-xs">{s.stepId}</span><StatusPill state={s.state} /></span>
      <span className="block">{s.name}</span>
      {s.reason && <span className="block text-xs text-muted">{s.reason}</span>}
      <span className="flex flex-wrap items-center gap-1 text-[11px] text-muted">
        {s.owner.length > 0 && <span>Owner: {s.owner.join(", ")}</span>}
        {tasks.length > 0 && <StatusPill state="open" label={`${tasks.length} task${tasks.length === 1 ? "" : "s"}`} />}
        {tasks.some((t) => !t.human) && <AgentChip name="can help" />}
        {tasks.some((t) => t.kind === "client") && <StatusPill state="client" label="Client" />}
        {items.length > 0 && <StatusPill state="before_release" label={`${items.length} open item${items.length === 1 ? "" : "s"}`} />}
        {stale.length > 0 && <StatusPill state="stale" label={`${stale.length} stale`} title={stale.map((w) => `${w.id}: ${w.stale?.why}`).join("; ")} />}
      </span>
    </button>
  );
}

function TaskRow({ a, open }: { a: Action; open: Open }) {
  const { catalogue } = useApp();
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2"><span className="text-xs text-muted">Task · {a.who || (a.human ? "a person" : "an agent can do it")}</span>
        {ownerKind(a) === "agent" ? <AgentChip /> : ownerKind(a) === "client" ? <StatusPill state="client" label="Client" /> : <StatusPill state="open" label="Person" />}</div>
      <p className={a.byAgent ? "text-agent" : undefined}>{a.what}</p>
      <div className="flex items-center justify-between gap-2"><span className="font-mono text-[11px] text-muted">{a.command}</span>
        <Act command={a.command} title={a.what} open={open} fixed={actionPayload(a, catalogue.get(a.command))} label="Open form…" /></div>
    </div>
  );
}

function StepDetail({ s, tasks, items, wps, id, open }: { s: StageStep; tasks: Action[]; items: OpenItem[]; wps: Workpaper[]; id: string; open: Open }) {
  const tpl = wps.filter((w) => s.templates.includes(w.template));
  return (
    <div className="space-y-4 text-sm">
      <p className="flex flex-wrap items-center gap-2"><StatusPill state={s.state} />{s.reason && <span className="text-muted">{s.reason}</span>}</p>
      {s.gate && <p><span className="font-medium">Gate:</span> {s.gate}</p>}
      <p><span className="font-medium">Owner:</span> {s.owner.join(", ") || "—"}</p>
      {s.gaps.length > 0 && (
        <section><h3 className="mb-1 font-medium">What is missing</h3>
          <ul className="space-y-1.5">{s.gaps.map((g, i) => <li key={i} className="flex flex-wrap items-center gap-2"><span className="min-w-0 flex-1">{g.text}{g.who ? ` · ${g.who}` : ""}{g.why ? <span className="block text-xs text-muted">{g.why}</span> : null}</span>
            {g.command && <Act command={g.command} title={g.text} open={open} label={`${g.command}…`} />}</li>)}</ul></section>
      )}
      <section><h3 className="mb-1 font-medium">Tasks in the engine's queue</h3><QueueList actions={tasks} open={open} empty="No task queued for this step." /></section>
      {items.length > 0 && <section><h3 className="mb-1 font-medium">Open items</h3><ul className="list-disc pl-4">{items.map((i) => <li key={i.key}>{i.text}</li>)}</ul></section>}
      {s.commands.length > 0 && (
        <section><h3 className="mb-1 font-medium">Engine actions for this step</h3>
          <div className="flex flex-wrap gap-1.5">{s.commands.map((c) => <Act key={c} command={c} title={`${s.stepId}: ${c}`} open={open} label={`${c}…`} />)}</div></section>
      )}
      {(tpl.length > 0 || s.templates.length > 0) && (
        <section><h3 className="mb-1 font-medium">Workpapers</h3>
          <ul className="space-y-1">{s.templates.map((t) => { const w = tpl.find((x) => x.template === t); return (
            <li key={t} className="flex flex-wrap items-center gap-2">{w ? <Link className="font-mono hover:underline" to={`/e/${id}/details/wp/${w.id}`}>{w.id}</Link> : <span className="font-mono text-muted">{t} (not opened)</span>}
              {w && <><StatusPill state={w.state} label={w.stateLabel} /><SignoffCircles signoffs={w.signoffs} scheme={w.signoffScheme} /></>}</li>); })}</ul></section>
      )}
      {s.standards.length > 0 && (
        <section><h3 className="mb-1 font-medium">Standards</h3><ul className="space-y-0.5 text-xs">{s.standards.map((x) => <li key={x.cite}><span className="font-mono">{x.cite}</span>{x.verified ? " · verified" : " · not verified against the primary text"}</li>)}</ul></section>
      )}
    </div>
  );
}

function Dependencies({ wps, id }: { wps: Workpaper[]; id: string }) {
  const byId = new Map(wps.map((w) => [w.id, w]));
  return (
    <Card title="Dependencies (the engine's graph)" bodyClass="p-3">
      <DataTable rows={wps} rowKey={(w) => w.id} caption="Workpaper dependencies" dense empty="No workpapers opened yet."
        filter={{ id: "stale", label: "freshness", value: (w) => (w.stale ? "Stale" : "Current") }}
        columns={[
          { id: "wp", header: "Workpaper", cell: (w) => <Link className="hover:underline" to={`/e/${id}/details/wp/${w.id}`}><span className="font-mono">{w.id}</span> <span className="text-muted">{w.title}</span></Link>, sortValue: (w) => w.id },
          { id: "state", header: "State", cell: (w) => <StatusPill state={w.state} label={w.stateLabel} /> },
          { id: "up", header: "Needs first", cell: (w) => w.predecessors.length ? (
            <ul className="space-y-0.5 text-xs">{w.predecessors.map((p) => { const u = byId.get(p); const n = w.needs.find((x) => x.dependsOn === p); return (
              <li key={p}><span className="font-mono">{p}</span>{u?.stale ? <span className="text-stale"> ↻ stale</span> : null}{n?.data ? <span className="text-muted"> — {n.data}</span> : null}</li>); })}</ul>) : <span className="text-xs text-muted">—</span> },
          { id: "cause", header: "Stale cause", cell: (w) => w.stale ? <span className="text-xs text-stale">↻ {w.stale.why}{w.stale.via ? ` (via ${w.stale.via})` : ""}</span> : <span className="text-xs text-muted">current</span> },
          { id: "down", header: "Used by", cell: (w) => <span className="font-mono text-xs">{w.consumers.join(", ") || "—"}</span> },
        ]} />
      <p className="mt-2 text-xs text-muted">A hub change (materiality, risk, extent, sample, confirmations, misstatements) marks its consumers stale; the engine refuses a sign-off on stale work until it is rendered again.</p>
    </Card>
  );
}
